// Auth store. Persists `session` to sessionStorage under `aero.auth`.
// `tenant` is host-resolved at boot, NOT persisted — it's looked up fresh on
// every page load via useTenantFromHost().
//
// The Playwright suite (AC4–AC6) reads/writes `sessionStorage["aero.auth"]`
// directly as a RAW AuthSession JSON blob (no zustand `{ state, version }`
// envelope). We do not use the `persist` middleware here — it would require
// either a custom PersistStorage wrapper to round-trip the bare blob OR a
// post-hydration migration step. Hand-rolling is simpler and easier to
// reason about for this small, load-bearing store.

import { create } from "zustand";
import type { AuthSession, AuthStatus, Tenant } from "./types.js";
import { SESSION_STORAGE_KEY } from "./config.js";

export interface AuthStoreState {
  session: AuthSession | null;
  tenant: Tenant | null;
  status: AuthStatus;

  setSession: (session: AuthSession) => void;
  clearSession: () => void;
  setTenant: (tenant: Tenant | null) => void;
  markWrongTenant: () => void;

  // SB6-AUTH-RECONNECT-01 — activity tracker writes here; the soft-prompt
  // logic in fetchWithRefresh reads `(Date.now() - lastActivityAt)` against
  // IDLE_MAX to decide whether to surface the modal or clear the session.
  setLastActivityAt: (ts: number) => void;
  /**
   * Update only the access-token half of the session (used by the
   * proactive refresh timer). Keeps `user` + `tenant` stable. No-op if
   * there is no current session.
   */
  setAccessToken: (input: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt: number;
  }) => void;
  /** Soft-expire the session: keeps tokens in store, flips status. */
  markSessionExpired: () => void;
  /** Clear the soft-expired flag after a successful re-auth. */
  clearSessionExpired: () => void;
}

function readPersisted(): AuthSession | null {
  if (typeof sessionStorage === "undefined") return null;
  const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

function writePersisted(session: AuthSession | null): void {
  if (typeof sessionStorage === "undefined") return;
  if (session === null) {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

const initialSession = readPersisted();

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  session: initialSession,
  tenant: null,
  status: initialSession ? "authenticated" : "idle",

  setSession: (session) => {
    writePersisted(session);
    set({ session, status: "authenticated" });
  },
  clearSession: () => {
    writePersisted(null);
    set({ session: null, status: "idle" });
  },
  setTenant: (tenant) => {
    set({ tenant });
  },
  markWrongTenant: () => {
    writePersisted(null);
    set({ session: null, status: "wrong_tenant" });
  },

  // SB6-AUTH-RECONNECT-01
  setLastActivityAt: (ts) => {
    const current = get().session;
    if (!current) return;
    const next: AuthSession = { ...current, lastActivityAt: ts };
    writePersisted(next);
    set({ session: next });
  },
  setAccessToken: ({ accessToken, refreshToken, accessTokenExpiresAt }) => {
    const current = get().session;
    if (!current) return;
    const next: AuthSession = {
      ...current,
      accessToken,
      refreshToken,
      accessTokenExpiresAt,
    };
    writePersisted(next);
    set({ session: next, status: "authenticated" });
  },
  markSessionExpired: () => {
    set({ status: "session_expired" });
  },
  clearSessionExpired: () => {
    if (get().status === "session_expired") {
      set({ status: "authenticated" });
    }
  },
}));

// Imperative-friendly access for non-React code (interceptors, route loaders).
export const authStore = {
  get: useAuthStore.getState,
  subscribe: useAuthStore.subscribe,
};
